/**
 * Durasan — warstwa ruchu.
 *
 * Cztery rzeczy, każda tania w wykonaniu:
 *  1. ujawnianie elementów przy wejściu w kadr (IntersectionObserver),
 *  2. powolny parallaks kadrów (jedna pętla rAF na całą stronę),
 *  3. liczniki liczb,
 *  4. stan przewinięcia strony dla nagłówka i paska postępu.
 *
 * Przy prefers-reduced-motion cały moduł kończy się natychmiast, a elementy
 * zostają ujawnione bez animacji — nikt nie zostaje z niewidoczną treścią.
 *
 * UWAGA na pętlę zwrotną: sekcje doładowywane przez Section Rendering API
 * obserwujemy MutationObserverem. Licznik, który co klatkę przepisywał
 * textContent, generował mutacje, te budziły obserwatora, obserwator zakładał
 * kolejne IntersectionObservery na te same liczniki, a każdy z nich startował
 * kolejne pętle rAF. Rosło to lawinowo i zabijało wątek główny — strona
 * zastygała. Dlatego teraz: obserwatory powstają RAZ, każdy element jest
 * pilnowany tylko raz (rejestry poniżej), a licznik pisze do istniejącego
 * węzła tekstowego, więc nie tworzy mutacji typu childList.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
document.documentElement.classList.add('du-motion-ready');

/* Rejestry elementów już objętych obsługą — chronią przed podwójną obsługą
   tego samego węzła przy kolejnych skanach DOM. */
const revealSeen = new WeakSet();
const countSeen = new WeakSet();
const parallaxSeen = new WeakSet();

let revealIO = null;
let countIO = null;

/* ------------------------------------------------------------------ */
/* 0. Kto tu właściwie przewija                                        */
/* ------------------------------------------------------------------ */

/**
 * Horizon NIE przewija okna. base.css ustawia
 * `html:has(.page-wrapper), html:has(.page-wrapper) body { height:100dvh; overflow:hidden }`,
 * więc pasek przewijania należy do .page-wrapper, a window.scrollY jest
 * na stałe równe 0. Wiązanie czegokolwiek ze zdarzeniem scroll okna
 * (zdarzenia scroll się nie propagują) sprawiało, że nagłówek nigdy nie
 * dostawał stanu przewinięcia, pasek postępu stał w miejscu, a parallaks
 * liczył się tylko raz — przy starcie.
 */
function getScroller() {
  return document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
}

function onScrollerScroll(handler) {
  const scroller = getScroller();
  const target = scroller === document.scrollingElement || scroller === document.documentElement ? window : scroller;
  target.addEventListener('scroll', handler, { passive: true });
  window.addEventListener('resize', handler, { passive: true });
}

function findWithin(root, selector) {
  const matches = [];
  if (root instanceof Element && root.matches(selector)) matches.push(root);
  if (root?.querySelectorAll) matches.push(...root.querySelectorAll(selector));
  return matches;
}

/* ------------------------------------------------------------------ */
/* 1. Ujawnianie przy przewijaniu                                      */
/* ------------------------------------------------------------------ */

function revealAll(root = document) {
  findWithin(root, '[data-du-reveal], [data-du-stagger]')
    .forEach((el) => el.classList.add('is-revealed'));
}

/**
 * Element, który obserwujemy, bywa inny niż ten, który odsłaniamy.
 *
 * Wiersz nagłówka w wariancie „mask" jest przesunięty o 100% w dół wewnątrz
 * rodzica z overflow:hidden. IntersectionObserver przycina obszar celu do
 * przodków, więc taki wiersz ma zawsze zerowe przecięcie z ekranem i NIGDY
 * sam się nie odsłoni — nagłówki po prostu nie pojawiały się na stronie.
 * Dlatego dla wariantu „mask" obserwujemy rodzica (samą maskę), a klasę
 * dokładamy do wiersza.
 */
const revealTargets = new WeakMap();

function watchFor(el) {
  return el.dataset.duReveal === 'mask' && el.parentElement ? el.parentElement : el;
}

function initReveal(root = document) {
  if (!revealIO) {
    revealIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const targets = revealTargets.get(entry.target) || [entry.target];
          targets.forEach((t) => t.classList.add('is-revealed'));
          revealIO.unobserve(entry.target);
          revealTargets.delete(entry.target);
        });
      },
      // Odpalamy lekko przed wejściem w kadr, żeby ruch był już zakończony,
      // zanim element znajdzie się na wysokości wzroku.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );
  }

  findWithin(root, '[data-du-reveal]:not(.is-revealed), [data-du-stagger]:not(.is-revealed)')
    .forEach((el) => {
      if (revealSeen.has(el)) return;
      revealSeen.add(el);

      const watched = watchFor(el);
      const list = revealTargets.get(watched);
      if (list) {
        list.push(el);
        return;
      }
      revealTargets.set(watched, [el]);
      revealIO.observe(watched);
    });
}

/* ------------------------------------------------------------------ */
/* 2. Parallaks                                                        */
/* ------------------------------------------------------------------ */

let parallaxLayers = [];
let parallaxBound = false;
let parallaxTicking = false;

function updateParallax() {
  if (REDUCED.matches) {
    parallaxLayers.forEach((layer) => layer.style.removeProperty('transform'));
    parallaxTicking = false;
    return;
  }

  parallaxLayers = parallaxLayers.filter((layer) => layer.isConnected);
  const vh = window.innerHeight;
  parallaxLayers.forEach((layer) => {
    const host = layer.parentElement;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    if (rect.bottom < -200 || rect.top > vh + 200) return;

    // -1..1 względem środka ekranu
    const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
    const strength = Number(layer.dataset.duParallax || 8);
    layer.style.transform = `translate3d(0, ${(progress * strength).toFixed(2)}%, 0)`;
  });
  parallaxTicking = false;
}

function onParallaxScroll() {
  if (parallaxTicking) return;
  parallaxTicking = true;
  window.requestAnimationFrame(updateParallax);
}

function initParallax(root = document) {
  findWithin(root, '.du-parallax').forEach((layer) => {
    if (parallaxSeen.has(layer)) return;
    parallaxSeen.add(layer);
    parallaxLayers.push(layer);
  });

  if (!parallaxLayers.length) return;

  if (!parallaxBound) {
    parallaxBound = true;
    onScrollerScroll(onParallaxScroll);
  }
  updateParallax();
}

/* ------------------------------------------------------------------ */
/* 3. Liczniki                                                         */
/* ------------------------------------------------------------------ */

/* Piszemy do jednego węzła tekstowego zamiast podmieniać textContent —
   inaczej każda klatka animacji byłaby mutacją childList i budziłaby
   obserwatora sekcji. */
function textNodeOf(el) {
  let node = el.firstChild;
  if (!node || node.nodeType !== 3) {
    node = document.createTextNode('');
    el.replaceChildren(node);
  }
  return node;
}

function runCounter(el) {
  const target = Number(el.dataset.duCount || 0);
  const node = textNodeOf(el);

  if (!Number.isFinite(target) || target <= 0) {
    node.nodeValue = el.dataset.duCount || '';
    return;
  }

  const duration = Number(el.dataset.duCountDuration || 1600);
  const start = performance.now();
  const format = new Intl.NumberFormat(document.documentElement.lang || 'pl-PL');

  const step = (now) => {
    const t = Math.min((now - start) / duration, 1);
    // easeOutExpo — szybki start, miękkie dojście do wartości
    const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    node.nodeValue = format.format(Math.round(target * eased));
    if (t < 1) window.requestAnimationFrame(step);
  };

  window.requestAnimationFrame(step);
}

function initCounters(root = document) {
  if (!countIO) {
    countIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          countIO.unobserve(entry.target);
          runCounter(entry.target);
        });
      },
      { threshold: 0.4 }
    );
  }

  findWithin(root, '[data-du-count]').forEach((el) => {
    if (countSeen.has(el)) return;
    countSeen.add(el);
    countIO.observe(el);
  });
}

/* ------------------------------------------------------------------ */
/* 4. Stan przewinięcia: nagłówek + pasek postępu                      */
/* ------------------------------------------------------------------ */

function initScrollState() {
  const root = document.documentElement;
  const bar = document.querySelector('.du-progress');
  let ticking = false;

  const update = () => {
    const scroller = getScroller();
    const y = scroller.scrollTop || window.scrollY || 0;
    const scrolled = y > 24;
    root.setAttribute('data-du-scrolled', scrolled ? 'true' : 'false');
    root.classList.toggle('scrolled', scrolled);

    if (bar) {
      const max = scroller.scrollHeight - scroller.clientHeight;
      root.style.setProperty('--du-progress', max > 0 ? String(Math.min(y / max, 1)) : '0');
    }
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  onScrollerScroll(onScroll);
  update();
}

/* ------------------------------------------------------------------ */
/* 5. Kolejność wierszy specyfikacji (PDP)                             */
/* ------------------------------------------------------------------ */

const SPEC_ORDER = [
  'Kształt',
  'Wymiar',
  'Możliwość regulowania wymiaru',
  'Wysokość',
  'Kolor profilu',
  'Grubość szkła',
  'Szyby',
  'Powłoka Nano Glass',
  'Rodzaj montażu',
  'Montaż',
  'Typ otwierania',
  'Uchwyt',
  'Rolki',
  'Profil',
  'Szerokość wejścia',
  'Dodatkowe',
  'Wysokość brodzika',
  'Korek w zestawie',
  'Szerokość',
  'Kolor',
  'Przepływ',
  'Średnica',
  'Materiał',
  'Kierunek wylotu',
  'Waga',
  'Sposób malowania',
  'Pokrywa',
  'Syfon',
];

const SPEC_ALIASES = {
  rozmiar: 'wymiar',
  wymiary: 'wymiar',
  'wysokość (cm)': 'wysokość',
  'kolor szkła': 'szyby',
  'kolor profili': 'kolor profilu',
  szkło: 'szyby',
  szklo: 'szyby',
  szyba: 'szyby',
  'grubość szkła (mm)': 'grubość szkła',
  'grubosc szkła': 'grubość szkła',
  powłoka: 'powłoka nano glass',
  'nano glass': 'powłoka nano glass',
  'powłoka nano': 'powłoka nano glass',
  'rodzaj drzwi': 'typ otwierania',
  wersja: 'montaż',
  'zakres regulacji': 'możliwość regulowania wymiaru',
  'kształt - rodzaj': 'kształt',
};

const specTableSeen = new WeakSet();

function normalizeSpecLabel(text) {
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .trim()
    .replace(/:$/, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function specOrderIndex(rawLabel, orderMap) {
  const label = normalizeSpecLabel(rawLabel);
  if (!label) return 9999;
  if (orderMap.has(label)) return orderMap.get(label);
  const aliased = SPEC_ALIASES[label];
  if (aliased && orderMap.has(aliased)) return orderMap.get(aliased);
  return 9999;
}

function reorderProductSpecs(root = document) {
  if (!document.body?.classList.contains('template-product')) return;

  const orderMap = new Map(
    SPEC_ORDER.map((label, index) => [normalizeSpecLabel(label), index]),
  );

  findWithin(root, '.du-product-overview__content table').forEach((table) => {
    if (specTableSeen.has(table)) return;

    const body = table.tBodies[0] || table;
    const rows = [...body.querySelectorAll(':scope > tr')];
    if (rows.length < 2) return;

    const labelFor = (row) => {
      const cell = row.querySelector('th') || row.cells?.[0];
      return cell ? cell.textContent : '';
    };

    const sorted = [...rows].sort((a, b) => {
      const aIdx = specOrderIndex(labelFor(a), orderMap);
      const bIdx = specOrderIndex(labelFor(b), orderMap);
      if (aIdx !== bIdx) return aIdx - bIdx;
      return rows.indexOf(a) - rows.indexOf(b);
    });

    const changed = sorted.some((row, index) => row !== rows[index]);
    if (!changed) {
      specTableSeen.add(table);
      return;
    }

    sorted.forEach((row) => body.appendChild(row));
    specTableSeen.add(table);
  });
}

/* ------------------------------------------------------------------ */
/* Start                                                               */
/* ------------------------------------------------------------------ */

function scan(root = document) {
  reorderProductSpecs(root);
  if (REDUCED.matches) {
    revealAll(root);
    return;
  }
  initReveal(root);
  initParallax(root);
  initCounters(root);
}

function boot() {
  // Stan przewinięcia działa zawsze — to nie jest ozdoba, tylko czytelność nagłówka.
  initScrollState();
  scan();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

/* Sekcje doładowywane przez Section Rendering API (edytor motywu) wnoszą nowe
   elementy — trzeba je objąć obserwatorem, inaczej zostaną niewidoczne.
   Reagujemy tylko na dołożone ELEMENTY i skanujemy najwyżej raz na klatkę. */
let scanScheduled = false;
const scanRoots = new Set();

function scheduleScan(root) {
  if (root) scanRoots.add(root);
  if (scanScheduled) return;
  scanScheduled = true;
  window.requestAnimationFrame(() => {
    scanScheduled = false;
    const roots = [...scanRoots];
    scanRoots.clear();
    roots.forEach((scanRoot) => scan(scanRoot));
  });
}

const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType === 1) {
        scheduleScan(node);
      }
    }
  }
});

if (document.body) {
  observer.observe(document.body, { childList: true, subtree: true });
}

REDUCED.addEventListener('change', (event) => {
  if (event.matches) {
    revealAll();
    parallaxLayers.forEach((layer) => layer.style.removeProperty('transform'));
    return;
  }

  scan();
});

/* ── Koszyk: kliknięcie w przyciemnione tło zamyka szufladę ──────────────────
   Na desktopie Horizon otwiera szufladę koszyka jako dialog NIE-modalny
   (`show()`, nie `showModal()`), więc nie ma warstwy ::backdrop i wbudowana
   obsługa `#onBackdropClick` w theme-drawer.js jest bezczynna — sama to zresztą
   deklaruje w komentarzu. My rozmywamy i przyciemniamy `.page-wrapper`, więc
   wygląda to jak modal i klient słusznie oczekuje, że kliknięcie obok zamknie
   koszyk (prośba Kamila 13.08).

   Słuchamy `pointerdown`, a nie `click`: naciśnięcie „Dodaj do koszyka" pada,
   ZANIM szuflada się otworzy, więc nie zamkniemy jej w tym samym geście. */
document.addEventListener('pointerdown', (event) => {
  const drawer = document.querySelector('theme-drawer#cart-drawer[open]');
  if (!drawer || typeof drawer.close !== 'function') return;

  const panel = drawer.querySelector('dialog');
  // W trybie modalnym przeglądarka i motyw radzą sobie same.
  if (!panel || panel.matches(':modal')) return;

  // Otwarty dialog w środku (np. potwierdzenie) ma pierwszeństwo.
  if (panel.querySelector('dialog[open]')) return;

  const target = event.target;
  if (!(target instanceof Element)) return;
  if (drawer.contains(target)) return;

  drawer.close();
});
